/**
 * Global Search – API client
 *
 * Talks to two Rust micro-services:
 *   • platform-search  – full-text search & typeahead
 *   • ai-orchestrator   – AI-powered chat (standard + SSE streaming)
 *
 * Uses the shared apiClient factory so every request gets Bearer auth,
 * X-Institution-ID, X-Request-ID, and 401 handling for free.
 */

import { createApiClient } from '@/services/apiClient';
import { tokenService } from '@/services/tokenService';
import type {
  SearchRequest,
  SearchResponse,
  TypeaheadRequest,
  TypeaheadResponse,
  AIChatRequest,
  AIChatResponse,
  AIChatStreamEvent,
} from '../types';

// ─── Axios instances ─────────────────────────────────────────────────────────

const searchClient = createApiClient(
  import.meta.env.VITE_SEARCH_API_URL || '/api/v1',
);

const aiClient = createApiClient(
  import.meta.env.VITE_AI_API_URL || '/api/v1',
);

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getInstitutionPrefix(): string {
  const id = tokenService.getInstitutionId();
  return id ? `/${id}` : '';
}

// ─── Platform-search endpoints ───────────────────────────────────────────────

/**
 * POST /{institution_id}/search — full-text search
 */
export async function search(req: SearchRequest): Promise<SearchResponse> {
  const prefix = getInstitutionPrefix();
  const { data } = await searchClient.post<SearchResponse>(
    `${prefix}/search`,
    req,
  );
  return data;
}

/**
 * POST /{institution_id}/search/typeahead — typeahead suggestions
 */
export async function typeahead(req: TypeaheadRequest): Promise<TypeaheadResponse> {
  const prefix = getInstitutionPrefix();
  const { data } = await searchClient.post<TypeaheadResponse>(
    `${prefix}/search/typeahead`,
    req,
  );
  return data;
}

// ─── AI-orchestrator endpoints ───────────────────────────────────────────────

/**
 * POST /{institution_id}/chat — standard (non-streaming) AI chat
 */
export async function chat(req: AIChatRequest): Promise<AIChatResponse> {
  const prefix = getInstitutionPrefix();
  const { data } = await aiClient.post<AIChatResponse>(
    `${prefix}/chat`,
    req,
  );
  return data;
}

/**
 * POST /{institution_id}/chat/stream — SSE streaming AI chat
 *
 * Uses native fetch + ReadableStream so we can process tokens as they arrive.
 * Returns an AbortController so callers can cancel the stream.
 */
export function chatStream(
  req: AIChatRequest,
  onEvent: (event: AIChatStreamEvent) => void,
  onError: (error: Error) => void,
  onComplete: () => void,
): AbortController {
  const controller = new AbortController();
  const prefix = getInstitutionPrefix();
  const baseUrl = import.meta.env.VITE_AI_API_URL || '/api/v1';
  const url = `${baseUrl}${prefix}/chat/stream`;

  const headers = tokenService.createAuthHeaders({
    Accept: 'text/event-stream',
  });

  fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(req),
    signal: controller.signal,
  })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`Stream request failed: ${response.status} ${response.statusText}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Response body is not readable');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        // Keep the last (possibly incomplete) line in the buffer
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || !trimmed.startsWith('data:')) continue;

          const json = trimmed.slice(5).trim();
          if (json === '[DONE]') {
            onEvent({ type: 'done', data: '' });
            continue;
          }

          try {
            const parsed: AIChatStreamEvent = JSON.parse(json);
            onEvent(parsed);
          } catch {
            // Non-JSON SSE line — emit as raw token
            onEvent({ type: 'token', data: json });
          }
        }
      }

      onComplete();
    })
    .catch((err: unknown) => {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      onError(err instanceof Error ? err : new Error(String(err)));
    });

  return controller;
}
